import { useMemo } from 'react';
import { Text } from '../../typography';
import InspectionItemText from './InspectionItemText';
import styles from './ConditionalInspectionItem.module.scss';
import SimpleUserCard from '../../SimpleUserCard';
import { evaluateCondition } from '../../../utilities/inspection';
import { actionsKeys } from '../../question_menu/conditionalActions/conditionalAction.utils';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import { useModal } from '../../../providers/ModalProvider';
import classNames from 'classnames';
import SimpleFileCard from '../../files/SimpleFileCard';
import { modals } from '../../../providers/modals';
import { useCurrentUser } from '../../../providers/UserProvider';
import { useNavigate } from 'react-router-dom';
import { getRoute } from '../../../constants/strings';
import { paths } from '../../../constants/strings';
import { FollowUpAssessment } from '../../modals/provider_modals/ConditionalActionModal';
import TextWithIcon from '../../typography/TextWithIcon';
import { customColors } from '../../../utilities';
import RichTextEditor from '../../rich_text/RichTextEditor';

export default function ConditionalInspectionItem({
  item,
  question,
  assessment = {},
  workspace,
  correctiveActions = [],
  noTopMargin = false,
  preview = false,
}) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { openConfirmationModal, openModal } = useModal();
  const { t } = useTranslation();
  const { conditionalAction = [], inputType } = question || {};
  const { assessmentTypes } = useCurrentUser();

  const matchingActions = useMemo(() => {
    const conditions = Array.isArray(conditionalAction)
      ? conditionalAction
      : [];
    if (preview) return conditions.flatMap((ca) => ca.actions ?? []);
    return conditions
      .filter(
        (ca) =>
          item?.value &&
          evaluateCondition(item.value, { inputType, conditionalAction: ca }),
      )
      .flatMap((ca) => ca.actions ?? []);
  }, [conditionalAction, inputType, item, preview]);

  const actionsToRender = {
    messages: [],
    files: [],
    assessments: [],
    questions: [],
    notifications: [],
  };

  for (const {
    id: actionId,
    action,
    actionable,
    additionalData,
    sortIndex,
  } of matchingActions) {
    const followUpInspection = assessment?.followUpInspections?.find(
      ({ templateId }) => templateId === actionable?.id,
    );
    const task = assessment?.createdTasks?.find(
      ({ templateId }) => templateId === actionable?.id,
    );

    switch (action) {
      case actionsKeys.followUpQuestion:
        actionsToRender.questions.push({ ...actionable, sortIndex });
        break;
      case actionsKeys.followUpTemplate:
        actionsToRender.assessments.push({
          followUpInspection,
          task,
          actionable,
          sortIndex,
        });
        break;
      case actionsKeys.showFile:
        actionsToRender.files.push({ ...actionable, sortIndex });
        break;
      case actionsKeys.popUp:
      case actionsKeys.showMessage:
        actionsToRender.messages.push({
          ...additionalData,
          actionId: actionId,
          sortIndex,
        });
        break;
      case actionsKeys.sendNotification:
        actionsToRender.notifications.push({ ...actionable, sortIndex });
        break;
      default:
        break;
    }
  }

  const { questions, assessments, notifications, messages, files } =
    actionsToRender;

  const allComplete = assessments?.every((a) => !!a.followUpInspection?.id);

  return (
    matchingActions.length > 0 && (
      <div
        className={classNames(
          styles.container,
          noTopMargin ? styles.noTopMargin : {},
        )}
      >
        {questions?.length > 0 && (
          <div>
            <Text noMargin weight="semiBold">
              {t(keys.conditionalActions.QUESTIONS)}
            </Text>
            <div className={styles.list}>
              {[...questions]
                ?.sort((a, b) => a?.sortIndex - b?.sortIndex)
                ?.map((actionable) => (
                  <div key={`question-action-${actionable?.id}`}>
                    <InspectionItemText
                      correctiveActions={correctiveActions}
                      refetchCPA={() => {}}
                      questionId={actionable?.id}
                      question={actionable}
                      inspectionId={assessment?.id}
                      inspectionVehicleId={assessment?.vehicle?.id}
                      vehicleUnitNumber={assessment?.vehicle?.unitNumber}
                      inspectionType={assessment?.inspectionType}
                      title={actionable.title}
                      description={actionable.description}
                      item={assessment?.items?.find(
                        ({ questionId, bundleId }) =>
                          questionId === actionable?.id &&
                          bundleId === item?.bundleId,
                      )}
                      additionalData={actionable.additionalData}
                      inspectionQuery={() => {}}
                      required={actionable.required}
                      readOnly={!assessment?.isDraft}
                      inputType={actionable.inputType}
                      inspectionTitle={assessment?.template?.title}
                      workspaceId={workspace?.id}
                      internalType={assessment?.template?.internalType}
                      refetch={() => {}}
                      inspection={assessment}
                      cpas={[]}
                      parentItem={item}
                      preview={preview}
                    />
                  </div>
                ))}
            </div>
          </div>
        )}
        {assessments?.length > 0 && (
          <div className={styles.border}>
            <TextWithIcon
              size="lg"
              icon={allComplete ? 'check_circle' : 'warning'}
              iconProps={{
                color: allComplete
                  ? customColors.GREEN_LIGHT
                  : customColors.RED_LIGHT,
              }}
              noMargin
              weight="semiBold"
            >
              {t(keys.conditionalActions.FOLLOW_UP_ASSESSMENTS)}
            </TextWithIcon>
            <div className={styles.list}>
              {[...assessments]
                ?.sort((a, b) => a?.sortIndex - b?.sortIndex)
                ?.map(({ actionable, followUpInspection, task }) => (
                  <div key={`assessment-action-${actionable.id}`}>
                    <FollowUpAssessment
                      template={actionable}
                      assessment={followUpInspection}
                      onClick={() =>
                        !followUpInspection
                          ? openModal({
                              modalName: modals.createAssessment,
                              variables: {
                                types: [
                                  Object.values(assessmentTypes).find(
                                    ({ key }) =>
                                      key === actionable?.templateType,
                                  ),
                                ],
                                dataWorkspaceId: assessment?.workspaceId,
                                type: Object.values(assessmentTypes).find(
                                  ({ key }) => key === actionable?.templateType,
                                ),
                                participants: [user],
                                template: actionable,
                                conditionalMetadata: {
                                  initialInspectionId: assessment.id,
                                  taskId: task?.id,
                                },
                              },
                            })
                          : navigate(
                              getRoute(
                                assessment?.workspaceId,
                                paths.assessment,
                                followUpInspection?.id,
                              ),
                            )
                      }
                    />
                  </div>
                ))}
            </div>
          </div>
        )}
        {files?.length > 0 && (
          <div className={styles.border}>
            <Text noMargin weight="semiBold">
              {t(keys.conditionalActions.FILES)}
            </Text>
            <Text noMargin size="sm" color="secondary">
              {t(keys.conditionalActions.FILES_SUBTEXT)}
            </Text>
            <div className={styles.list}>
              {[...files]
                ?.sort((a, b) => a?.sortIndex - b?.sortIndex)
                ?.map((file) => (
                  <div key={`file-action-${file?.id}`}>
                    <SimpleFileCard
                      file={file}
                      extension={file?.extension}
                      fileName={file?.name}
                    />
                  </div>
                ))}
            </div>
          </div>
        )}
        {notifications?.length > 0 && (
          <div className={styles.border}>
            <div>
              <TextWithIcon
                size="lg"
                icon="check_circle"
                iconProps={{ color: customColors.GREEN_LIGHT }}
                noMargin
                weight="semiBold"
              >
                {t(keys.conditionalActions.NOTIFICATIONS)}
              </TextWithIcon>
            </div>
            <div className={styles.list}>
              {[...notifications]
                ?.sort((a, b) => a?.sortIndex - b?.sortIndex)
                ?.map((actionable) => (
                  <div
                    className={styles.userContainer}
                    key={`notify-action-${actionable?.id}`}
                  >
                    <SimpleUserCard user={actionable} />
                  </div>
                ))}
            </div>
          </div>
        )}
        {messages?.length > 0 && (
          <div className={styles.border}>
            <div>
              <TextWithIcon
                size="lg"
                icon="announcement"
                iconProps={{ color: customColors.YELLOW }}
                noMargin
                weight="semiBold"
              >
                {t(keys.conditionalActions.MESSAGES)}
              </TextWithIcon>
              <Text noMargin color="secondary" size="sm">
                {t(keys.conditionalActions.MESSAGE_SUBTEXT)}
              </Text>
            </div>
            <div className={styles.list}>
              {[...messages]
                ?.sort((a, b) => a?.sortIndex - b?.sortIndex)
                ?.map(({ title, message, actionId, files }) =>
                  !title ? (
                    <RichTextEditor readOnly value={message} />
                  ) : (
                    <div key={`message-action-${actionId}`}>
                      <Text
                        hover
                        noMargin
                        weight="semiBold"
                        color="accentPrimary"
                        onClick={() =>
                          openConfirmationModal({
                            title,
                            description: message,
                            files,
                            hideSubmit: true,
                            variant: 'warning',
                            cancelText: 'Close',
                          })
                        }
                      >
                        {title}
                      </Text>
                    </div>
                  ),
                )}
            </div>
          </div>
        )}
      </div>
    )
  );
}
